import { type SettingScreen, type SettingScreenPolicy } from '../atoms'

import type { SetupOtpMode } from '@root/features/auth/setup-otp/ui'
import type { TransKey } from '@root/shared/localization'

export function getEmbeddedPolicyUrl(
  policiesHost: string,
  policy: SettingScreenPolicy,
) {
  return `${policiesHost}/embedded/${policy}.html`
}

type SettingsHeaderProps = {
  screen: SettingScreen | undefined
  optMode: SetupOtpMode
  setOptMode: (mode: SetupOtpMode) => void
  setScreen: (screen: SettingScreen) => void
}

type SettingsOption = [TransKey, undefined | (() => void)]

export const SETTINGS_HEADER_MAP = ({
  screen,
  optMode,
  setOptMode,
  setScreen,
}: SettingsHeaderProps): SettingsOption => {
  if (!screen) {
    return ['settings', undefined]
  }

  const handleSetupOtpBack = () =>
    optMode === 'instructions'
      ? () => setScreen('main')
      : () => setOptMode('instructions')

  const record: Record<SettingScreen, SettingsOption> = {
    main: ['settings', undefined],
    security: ['settings.security', () => setScreen('main')],
    shortcuts: ['shortcuts', () => setScreen('main')],
    sessions: ['sessions', () => setScreen('security')],
    language: ['language', () => setScreen('main')],
    'account-and-privacy': [
      'settings.account-and-privacy',
      () => setScreen('main'),
    ],
    'setup-otp': ['mfa.two_factory_auth', handleSetupOtpBack()],
    'update-password': ['settings.password.title', () => setScreen('main')],
    'update-local-password': [
      'settings.local-password.title',
      () => setScreen('main'),
    ],
    'app-settings': ['settings.app-settings', () => setScreen('main')],
    'trading-mode': ['settings.trading-mode', () => setScreen('main')],
    'app-settings-theme': [
      'settings.app-settings.theme',
      () => setScreen('app-settings'),
    ],
    'app-settings-custom-theme': [
      'theme.custom.name',
      () => setScreen('app-settings-theme'),
    ],
    // Policies
    policies: ['settings.about-the-app', () => setScreen('main')],
    'terms-of-service': [
      'policies.terms-of-service',
      () => setScreen('policies'),
    ],
    'privacy-policy': ['policies.privacy-policy', () => setScreen('policies')],
    'anti-money-laundering-policy': [
      'policies.anti-money-laundering-policy',
      () => setScreen('policies'),
    ],
    'boosting-terms': ['policies.boosting-terms', () => setScreen('policies')],
    'disclaimer-terms': [
      'policies.disclaimer-terms',
      () => setScreen('policies'),
    ],
    'affiliate-program-terms': [
      'policies.affiliate-program-terms',
      () => setScreen('policies'),
    ],
    'live-streaming-terms': [
      'policies.live-streaming-terms',
      () => setScreen('policies'),
    ],
    'token-creation-and-promotion-terms': [
      'policies.token-creation-and-promotion-terms',
      () => setScreen('policies'),
    ],
    'legal-entity-access-terms': [
      'policies.legal-entity-access-terms',
      () => setScreen('policies'),
    ],
  }

  return record[screen]
}
